import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectLatestRoute, selectLoadingLatestRoute } from '../../../features/tripcenter/routeSlice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

const RouteNotificationsModal = ({ open, onClose, onSubmit, initialNotifications }) => {
    const latestRoute = useSelector(selectLatestRoute);
    const loadingLatestRoute = useSelector(selectLoadingLatestRoute);

    // Determine if we're in edit mode
    const isEditMode = !!initialNotifications;

    // Initialize notifications state
    const [notifications, setNotifications] = useState(() => {
        const defaultNotifications = {
            sms: [],
            whatsApp: [],
            telegram: [],
            email: [],
            push: []
        };
        return defaultNotifications;
    });

    useEffect(() => {
        if (isEditMode) {
            // Edit mode: Use provided notifications with telegram array
            //console.log("Edit mode - Using provided notifications:", initialNotifications);
            setNotifications({
                ...initialNotifications,
                telegram: initialNotifications.telegram || [] // Ensure telegram array exists
            });
        } else {
            // Create mode: Use latest route notifications if available
            if (!loadingLatestRoute && latestRoute?.notifications) {
                //console.log("Create mode - Using latest route notifications");
                setNotifications({
                    ...latestRoute.notifications,
                    telegram: latestRoute.notifications.telegram || [] // Ensure telegram array exists
                });
            }
        }
    }, [open, initialNotifications, latestRoute, loadingLatestRoute, isEditMode]);

    //console.log("latestRoute Notifications", latestRoute?.notifications ?? "No Notifications");
    // Define category options
    const categoryOptions = {
        main: {
            activeStatus: 'Everything'
        },
        sub: {
            ruleViolation: 'Rules',
            tripStage: 'Overview',
            finalReport: 'Final Report'
        }
    };



    // Add validation helper functions
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const isValidPhone = (phone) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    // Add state for validation errors
    const [validationErrors, setValidationErrors] = useState({});

    // Add a function to check if a notification row is complete
    const isNotificationComplete = (notification) => {
        const hasName = !!notification.name?.trim();
        const hasContact = notification.email
            ? isValidEmail(notification.email)
            : isValidPhone(notification.number);
        const hasCategories = notification.categories?.length > 0;

        return hasName && hasContact && hasCategories;
    };

    // Handler for adding new notification row
    const handleAddRow = (type) => {
        setNotifications(prev => {
            const newIndex = prev[type].length;
            const newNotifications = {
                ...prev,
                [type]: [...prev[type], {
                    name: '',
                    [type === 'email' ? 'email' : 'number']: '',
                    categories: []
                }]
            };

            // Mark new row as incomplete
            setIncompleteRows(prev => ({
                ...prev,
                [`${type}-${newIndex}`]: true
            }));

            return newNotifications;
        });
    };

    // Handler for removing notification row
    const handleRemoveRow = (type, index) => {
        setNotifications(prev => ({
            ...prev,
            [type]: prev[type].filter((_, i) => i !== index)
        }));
    };

    // Handler for updating notification fields
    const handleChange = (type, index, field, value) => {
        // Clear existing validation error for this field
        setValidationErrors(prev => ({
            ...prev,
            [`${type}-${index}-${field}`]: ''
        }));

        // Validate phone number or email
        if (field === 'number' || field === 'email') {
            let isValid = true;
            let errorMessage = '';

            if (field === 'email') {
                isValid = isValidEmail(value);
                errorMessage = isValid ? '' : 'Invalid email address';
            } else if (field === 'number') {
                isValid = isValidPhone(value);
                errorMessage = isValid ? '' : 'Phone number must be 10 digits';
            }

            setValidationErrors(prev => ({
                ...prev,
                [`${type}-${index}-${field}`]: errorMessage
            }));
        }

        // Update the notifications state
        setNotifications(prev => {
            const newNotifications = {
                ...prev,
                [type]: prev[type].map((item, i) =>
                    i === index ? { ...item, [field]: value } : item
                )
            };

            // Update incomplete status
            const updatedRow = newNotifications[type][index];
            setIncompleteRows(prev => ({
                ...prev,
                [`${type}-${index}`]: !isNotificationComplete(updatedRow)
            }));

            return newNotifications;
        });
    };

    // Handler for updating categories
    const handleCategoryChange = (type, index, category) => {
        setNotifications(prev => ({
            ...prev,
            [type]: prev[type].map((item, i) => {
                if (i === index) {
                    let newCategories = [...item.categories];

                    if (category === 'activeStatus') {
                        // If Everything is being selected
                        if (!item.categories.includes(category)) {
                            newCategories = ['activeStatus', 'ruleViolation', 'tripStage', 'finalReport'];
                        } else {
                            // If Everything is being unselected, remove everything
                            newCategories = [];
                        }
                    } else {
                        // Handling sub-categories
                        if (item.categories.includes(category)) {
                            // Remove the category
                            newCategories = newCategories.filter(c => c !== category);
                            // Remove Everything if it was selected
                            newCategories = newCategories.filter(c => c !== 'activeStatus');
                        } else {
                            // Add the category
                            newCategories.push(category);
                            // Check if all sub-categories are now selected
                            const allSubCategories = Object.keys(categoryOptions.sub);
                            const hasAllSubs = allSubCategories.every(subCat =>
                                newCategories.includes(subCat)
                            );
                            if (hasAllSubs) {
                                newCategories.push('activeStatus');
                            }
                        }
                    }

                    const updatedItem = { ...item, categories: newCategories };

                    // Update incomplete status after category change
                    setIncompleteRows(prev => ({
                        ...prev,
                        [`${type}-${index}`]: !isNotificationComplete(updatedItem)
                    }));

                    return updatedItem;
                }
                return item;
            })
        }));
    };

    // Modify handleSubmit to ensure categories are present
    const handleSubmit = () => {
        // Filter out incomplete rows from each notification type
        const completeNotifications = Object.entries(notifications).reduce((acc, [type, recipients]) => {
            const completeRecipients = recipients.filter(recipient => {
                const isComplete = isNotificationComplete(recipient);
                if (!isComplete) {
                    setIncompleteRows(prev => ({
                        ...prev,
                        [`${type}-${recipients.indexOf(recipient)}`]: true
                    }));
                }
                return isComplete;
            });

            if (completeRecipients.length > 0) {
                acc[type] = completeRecipients;
            }
            return acc;
        }, {});

        // Check for validation errors in complete rows
        let hasErrors = false;
        const newValidationErrors = {};

        Object.entries(completeNotifications).forEach(([type, recipients]) => {
            recipients.forEach((recipient, index) => {
                if (type === 'email') {
                    if (!isValidEmail(recipient.email)) {
                        newValidationErrors[`${type}-${index}-email`] = 'Invalid email address';
                        hasErrors = true;
                    }
                } else {
                    if (!isValidPhone(recipient.number)) {
                        newValidationErrors[`${type}-${index}-number`] = 'Phone number must be 10 digits';
                        hasErrors = true;
                    }
                }
            });
        });

        if (hasErrors) {
            setValidationErrors(newValidationErrors);
            return;
        }
        console.log("completeNotifications", completeNotifications);
        onSubmit(completeNotifications);
    };

    // Add state for tracking incomplete rows
    const [incompleteRows, setIncompleteRows] = useState({});

    if (loadingLatestRoute) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2 }}>Loading Notifications data...</Typography>
            </Box>
        );
    } else {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {isEditMode ? 'Edit Route Notifications' : 'Define Route Notifications'}
                    </Typography>
                    <Divider sx={{ mt: 1 }} />
                </DialogTitle>
                <DialogContent>
                    {/* Notification sections */}
                    {Object.entries(notifications).map(([type, recipients]) => (
                        <Box key={type} sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {type.charAt(0).toUpperCase() + type.slice(1)} Notifications
                            </Typography>

                            {recipients.map((recipient, index) => (
                                <Box key={index} sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 2fr auto',  // Three columns: contact info, rules, and delete button
                                    gap: 3,
                                    mb: 2,
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: incompleteRows[`${type}-${index}`] ? 'warning.main' : 'divider',
                                    position: 'relative',
                                    minHeight: '120px',  // Set minimum height for consistency
                                }}>
                                    {/* Contact Information Box */}
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        height: '100%',
                                        bgcolor: 'grey.50',
                                        p: 2,
                                        borderRadius: 1,
                                        justifyContent: 'center'  // Center content vertically
                                    }}>
                                        <TextField
                                            label="Name"
                                            value={recipient.name}
                                            onChange={(e) => handleChange(type, index, 'name', e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                        <TextField
                                            label={type === 'email' ? 'Email' : 'Number'}
                                            value={recipient[type === 'email' ? 'email' : 'number']}
                                            onChange={(e) => handleChange(type, index, type === 'email' ? 'email' : 'number', e.target.value)}
                                            size="small"
                                            fullWidth
                                            error={!!validationErrors[`${type}-${index}-${type === 'email' ? 'email' : 'number'}`]}
                                            helperText={validationErrors[`${type}-${index}-${type === 'email' ? 'email' : 'number'}`]}
                                            inputProps={{
                                                pattern: type === 'email' ? undefined : '[0-9]*',
                                                inputMode: type === 'email' ? 'email' : 'numeric',
                                            }}
                                        />
                                    </Box>

                                    {/* Rules Selection Box */}
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 2fr',  // Split into two columns
                                        gap: 2,
                                        height: '100%',
                                        bgcolor: 'grey.50',  // Light background
                                        p: 2,
                                        borderRadius: 1,
                                    }}>
                                        {/* Left Section - Label */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRight: 1,
                                            borderColor: 'divider',
                                            pr: 2
                                        }}>
                                            <Typography
                                                variant="subtitle2"
                                                color="text.secondary"
                                                sx={{ fontWeight: 'medium' }}
                                            >
                                                Choose Notification Types
                                            </Typography>
                                        </Box>

                                        {/* Right Section - Checkboxes */}
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}>
                                            {/* Everything checkbox */}
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={recipient.categories.includes('activeStatus')}
                                                        onChange={() => handleCategoryChange(type, index, 'activeStatus')}
                                                        size="small"
                                                    />
                                                }
                                                label={categoryOptions.main.activeStatus}
                                            />

                                            {/* Sub-category checkboxes */}
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 2,
                                                ml: 0  // Indent sub-categories
                                            }}>
                                                {Object.entries(categoryOptions.sub).map(([value, label]) => (
                                                    <FormControlLabel
                                                        key={value}
                                                        control={
                                                            <Checkbox
                                                                checked={recipient.categories.includes(value)}
                                                                onChange={() => handleCategoryChange(type, index, value)}
                                                                size="small"
                                                            />
                                                        }
                                                        label={label}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Delete Button Box */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',  // Center vertically
                                        justifyContent: 'center',
                                        height: '100%',
                                        bgcolor: 'grey.50',
                                        borderRadius: 1,
                                        px: 1  // Add some horizontal padding
                                    }}>
                                        <IconButton
                                            onClick={() => handleRemoveRow(type, index)}
                                            color="error"
                                            sx={{
                                                padding: 1.5,  // Make button bigger
                                                '&:hover': {
                                                    backgroundColor: 'error.light',
                                                    color: 'error.contrastText'
                                                }
                                            }}
                                        >
                                            <DeleteIcon sx={{ fontSize: '1.5rem' }} />  {/* Make icon bigger */}
                                        </IconButton>
                                    </Box>

                                    {incompleteRows[`${type}-${index}`] && (
                                        <Typography
                                            variant="caption"
                                            color="warning.main"
                                            sx={{
                                                position: 'absolute',
                                                bottom: -20,
                                                left: 8
                                            }}
                                        >
                                            * Complete all fields to include in notifications
                                        </Typography>
                                    )}
                                </Box>
                            ))}

                            <Button
                                startIcon={<AddIcon />}
                                onClick={() => handleAddRow(type)}
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1 }}
                            >
                                Add {type} Notification
                            </Button>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {isEditMode ? 'Save Changes' : 'Set Notifications'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
};

export default RouteNotificationsModal; 